import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef } from 'react';
import { useListServiceProviders } from '../../../services/automation.queries';

export interface IEditStep4FormValues {
  assigneeId: number | null;
}

interface IEditStepFourProps {
  initialAssigneeId?: number | null;
  isActive: boolean;
  onSubmit: (data: IEditStep4FormValues) => void;
  submitRef: React.MutableRefObject<(() => void) | null>;
}

const EditStepFour = ({ initialAssigneeId, isActive, onSubmit, submitRef }: IEditStepFourProps) => {
  const { t } = useTranslation();

  const { data: providersData, isLoading } = useListServiceProviders();
  const providers = providersData?.result ?? [];

  const [selectedId, setSelectedId] = useState<number | null>(initialAssigneeId ?? null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Expose submit to parent via ref — only when this step is active
  useEffect(() => {
    if (!isActive) return;
    submitRef.current = () => {
      // Assignment is optional per Figma, so always allow submit
      onSubmit({ assigneeId: selectedId });
    };
  }, [isActive, selectedId, onSubmit, submitRef]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedProvider = providers.find((p) => p.id === selectedId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8" style={{ border: '2px solid #E8E8EA', borderTopColor: '#5B3FFF' }} />
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ gap: 16 }}>
      {/* Label */}
      <div className="flex items-center justify-end">
        <p
          style={{
            fontFamily: 'Tajawal, sans-serif',
            fontWeight: 700,
            fontSize: 16,
            color: '#625783',
            margin: 0,
            textAlign: 'start',
            width: '100%',
          }}
        >
          {t('Automation.assignToOptional')}
        </p>
      </div>

      {/* Dropdown */}
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="w-full cursor-pointer outline-none"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#FFFFFF',
            border: '1px solid #EAEEF4',
            borderRadius: 12,
            padding: '12px 14px',
            gap: 4,
          }}
        >
          <p
            style={{
              fontFamily: 'Tajawal, sans-serif',
              fontWeight: 500,
              fontSize: 14,
              color: selectedProvider ? '#331860' : '#BDBDBD',
              margin: 0,
              flex: 1,
              textAlign: 'start',
            }}
          >
            {selectedProvider ? selectedProvider.fullName : t('Automation.selectResponsible')}
          </p>

          {/* Chevron icon */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              flexShrink: 0,
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <path
              d="M4 6L8 10L12 6"
              stroke="#667085"
              strokeWidth="1.33"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              backgroundColor: '#FFFFFF',
              border: '1px solid #EAEEF4',
              borderRadius: 12,
              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
              zIndex: 10,
              maxHeight: 200,
              overflowY: 'auto',
            }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center" style={{ padding: 16 }}>
                <div className="animate-spin rounded-full h-5 w-5" style={{ border: '2px solid #E8E8EA', borderTopColor: '#5B3FFF' }} />
              </div>
            ) : providers.length === 0 ? (
              <div style={{ padding: '12px 14px' }}>
                <p
                  style={{
                    fontFamily: 'Tajawal, sans-serif',
                    fontSize: 14,
                    color: '#8E98A1',
                    margin: 0,
                    textAlign: 'center',
                  }}
                >
                  —
                </p>
              </div>
            ) : (
              providers.map((provider) => {
                const isSelected = provider.id === selectedId;
                return (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => {
                      setSelectedId(isSelected ? null : provider.id);
                      setIsOpen(false);
                    }}
                    className="w-full cursor-pointer outline-none"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 14px',
                      backgroundColor: isSelected ? 'rgba(239, 236, 255, 0.3)' : 'transparent',
                      borderTop: 'none',
                      borderLeft: 'none',
                      borderRight: 'none',
                      borderBottom: '1px solid #F2F2F2',
                      gap: 8,
                      textAlign: 'start',
                    }}
                  >
                    <p
                      style={{
                        fontFamily: 'Tajawal, sans-serif',
                        fontWeight: isSelected ? 700 : 500,
                        fontSize: 14,
                        color: isSelected ? '#5B3FFF' : '#331860',
                        margin: 0,
                        flex: 1,
                      }}
                    >
                      {provider.fullName}
                    </p>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EditStepFour;
